import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { UserCog, Trash2, Users, ShieldCheck, Eye, Plus, KeyRound } from "lucide-react";
import { toast } from "sonner";

const roleLabels = { admin: "Administrateur", commercial: "Commercial", client: "Client", webmaster: "WebMaster" };
const roleColors = { admin: "bg-orange-50 text-orange-600", commercial: "bg-blue-50 text-blue-600", client: "bg-slate-100 text-slate-600", webmaster: "bg-purple-50 text-purple-600" };

export default function UsersPage() {
  const { apiFetch, API, user: currentUser, impersonate } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const isWebmaster = currentUser?.role === "webmaster";

  // Create user state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", email: "", password: "", role: "admin" });
  const [creating, setCreating] = useState(false);

  // Change password state
  const [pwdDialogOpen, setPwdDialogOpen] = useState(false);
  const [pwdTarget, setPwdTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [changingPwd, setChangingPwd] = useState(false);

  const fetchUsers = useCallback(async () => {
    const res = await apiFetch(`${API}/users`);
    if (res.ok) setUsers(await res.json());
    setLoading(false);
  }, [apiFetch, API]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const updateRole = async (userId, newRole) => {
    const res = await apiFetch(`${API}/users/${userId}`, { method: "PUT", body: JSON.stringify({ role: newRole }) });
    if (res.ok) { toast.success("Rôle mis à jour"); fetchUsers(); }
    else { const err = await res.json(); toast.error(err.detail); }
  };

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Supprimer l'utilisateur ${email} ?`)) return;
    const res = await apiFetch(`${API}/users/${userId}`, { method: "DELETE" });
    if (res.ok) { toast.success("Utilisateur supprimé"); fetchUsers(); }
    else { const err = await res.json(); toast.error(err.detail); }
  };

  const handleImpersonate = async (userId, name) => {
    if (!window.confirm(`Se connecter en tant que ${name} ?`)) return;
    try {
      const res = await apiFetch(`${API}/auth/impersonate/${userId}`, { method: "POST" });
      if (res.ok) {
        const data = await res.json();
        impersonate(data.token, data.user);
        toast.success(`Connecté en tant que ${name}`);
        navigate("/dashboard");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur");
      }
    } catch (e) { toast.error("Erreur d'impersonation"); }
  };

  const handleCreateUser = async () => {
    if (!createForm.name || !createForm.email || !createForm.password) {
      toast.error("Tous les champs sont requis");
      return;
    }
    if (createForm.password.length < 6) {
      toast.error("Le mot de passe doit faire au moins 6 caractères");
      return;
    }
    setCreating(true);
    try {
      const res = await apiFetch(`${API}/users/create`, {
        method: "POST",
        body: JSON.stringify(createForm),
      });
      if (res.ok) {
        toast.success(`Utilisateur ${createForm.name} créé`);
        setCreateDialogOpen(false);
        setCreateForm({ name: "", email: "", password: "", role: "admin" });
        fetchUsers();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur");
      }
    } catch (e) { toast.error("Erreur de création"); }
    finally { setCreating(false); }
  };

  const openPwdDialog = (user) => {
    setPwdTarget(user);
    setNewPassword("");
    setPwdDialogOpen(true);
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Le mot de passe doit faire au moins 6 caractères");
      return;
    }
    setChangingPwd(true);
    try {
      const res = await apiFetch(`${API}/users/${pwdTarget.user_id}/password`, {
        method: "PUT",
        body: JSON.stringify({ password: newPassword }),
      });
      if (res.ok) {
        toast.success(`Mot de passe modifié pour ${pwdTarget.name}`);
        setPwdDialogOpen(false);
        setPwdTarget(null);
        setNewPassword("");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur");
      }
    } catch (e) { toast.error("Erreur"); }
    finally { setChangingPwd(false); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "-";

  const teamUsers = users.filter(u => ["webmaster", "admin", "commercial"].includes(u.role));
  const clientUsers = users.filter(u => u.role === "client");

  if (loading) return <div className="p-4 sm:p-8"><div className="animate-pulse h-96 bg-slate-200 rounded-lg" /></div>;

  const UserTable = ({ userList, showRoleEdit }) => (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-xs font-semibold">Nom</TableHead>
            <TableHead className="text-xs font-semibold hidden sm:table-cell">Email</TableHead>
            <TableHead className="text-xs font-semibold hidden md:table-cell">Société</TableHead>
            <TableHead className="text-xs font-semibold hidden lg:table-cell">Inscrit le</TableHead>
            <TableHead className="text-xs font-semibold">Rôle</TableHead>
            {isWebmaster && <TableHead className="text-xs font-semibold w-20"></TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {userList.map(u => (
            <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
              <TableCell>
                <div>
                  <p className="text-sm font-medium">{u.name}</p>
                  <p className="text-xs text-slate-400 sm:hidden">{u.email}</p>
                </div>
              </TableCell>
              <TableCell className="text-sm text-slate-500 hidden sm:table-cell">{u.email}</TableCell>
              <TableCell className="text-sm text-slate-500 hidden md:table-cell">{u.company_name || "-"}</TableCell>
              <TableCell className="text-sm text-slate-500 hidden lg:table-cell">{formatDate(u.created_at)}</TableCell>
              <TableCell>
                {isWebmaster && showRoleEdit ? (
                  <Select value={u.role} onValueChange={v => updateRole(u.user_id, v)}>
                    <SelectTrigger className="h-7 text-xs w-28 sm:w-32 border-0" data-testid={`user-role-${u.user_id}`}>
                      <Badge className={`${roleColors[u.role] || "bg-slate-100 text-slate-600"} text-[10px] border-0`}>{roleLabels[u.role] || u.role}</Badge>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="webmaster">WebMaster</SelectItem>
                      <SelectItem value="admin">Administrateur</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                      <SelectItem value="client">Client</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge className={`${roleColors[u.role] || "bg-slate-100 text-slate-600"} text-[10px] border-0`}>{roleLabels[u.role] || u.role}</Badge>
                )}
              </TableCell>
              {isWebmaster && (
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openPwdDialog(u)} className="h-7 w-7 p-0 text-amber-400 hover:text-amber-600" title="Modifier le mot de passe" data-testid={`pwd-user-${u.user_id}`}>
                      <KeyRound className="w-3.5 h-3.5" />
                    </Button>
                    {u.user_id !== currentUser?.user_id && (
                      <Button variant="ghost" size="sm" onClick={() => handleImpersonate(u.user_id, u.name)} className="h-7 w-7 p-0 text-violet-400 hover:text-violet-600" title="Se connecter en tant que" data-testid={`impersonate-user-${u.user_id}`}>
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => deleteUser(u.user_id, u.email)} className="h-7 w-7 p-0 text-red-400 hover:text-red-600" data-testid={`delete-user-${u.user_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </TableCell>
              )}
            </TableRow>
          ))}
          {userList.length === 0 && (
            <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">Aucun utilisateur</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <div className="p-4 sm:p-8" data-testid="users-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-barlow text-xl sm:text-2xl font-bold text-slate-900 tracking-tight uppercase">Utilisateurs</h1>
          <p className="text-sm text-slate-500 mt-1">
            {isWebmaster ? "Gérez les accès et les rôles des utilisateurs" : "Liste des utilisateurs"}
          </p>
        </div>
        {isWebmaster && (
          <Button onClick={() => setCreateDialogOpen(true)} className="bg-[#1a3a5c] hover:bg-[#15304d] gap-2" data-testid="create-user-btn">
            <Plus className="w-4 h-4" /> Créer un utilisateur
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: "WebMasters", count: users.filter(u => u.role === "webmaster").length, color: "text-purple-600" },
          { label: "Admins", count: users.filter(u => u.role === "admin").length, color: "text-orange-600" },
          { label: "Commerciaux", count: users.filter(u => u.role === "commercial").length, color: "text-blue-600" },
          { label: "Clients", count: clientUsers.length, color: "text-slate-600" },
        ].map(s => (
          <Card key={s.label} className="border-slate-200">
            <CardContent className="p-3 sm:p-4 flex items-center gap-3">
              <div className={`text-xl sm:text-2xl font-bold font-barlow ${s.color}`}>{s.count}</div>
              <div className="text-xs text-slate-500">{s.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="team">
        <TabsList className="mb-4">
          <TabsTrigger value="team" className="gap-1.5" data-testid="tab-team">
            <ShieldCheck className="w-3.5 h-3.5" /> Équipe ({teamUsers.length})
          </TabsTrigger>
          <TabsTrigger value="clients" className="gap-1.5" data-testid="tab-clients">
            <Users className="w-3.5 h-3.5" /> Clients ({clientUsers.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="team">
          <Card className="border-slate-200">
            <CardContent className="p-0">
              <UserTable userList={teamUsers} showRoleEdit={true} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="clients">
          <Card className="border-slate-200">
            <CardContent className="p-0">
              <UserTable userList={clientUsers} showRoleEdit={true} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow text-lg uppercase tracking-tight">Créer un utilisateur</DialogTitle>
            <DialogDescription>Créez un nouvel utilisateur avec accès par email et mot de passe</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Nom complet *</Label>
              <Input value={createForm.name} onChange={e => setCreateForm(s => ({ ...s, name: e.target.value }))} placeholder="Jean Dupont" data-testid="create-user-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Email *</Label>
              <Input type="email" value={createForm.email} onChange={e => setCreateForm(s => ({ ...s, email: e.target.value }))} placeholder="jean@exemple.com" data-testid="create-user-email" />
            </div>
            <div className="space-y-1.5">
              <Label>Mot de passe * (min. 6 caractères)</Label>
              <Input type="password" value={createForm.password} onChange={e => setCreateForm(s => ({ ...s, password: e.target.value }))} placeholder="••••••" data-testid="create-user-password" />
            </div>
            <div className="space-y-1.5">
              <Label>Rôle</Label>
              <Select value={createForm.role} onValueChange={v => setCreateForm(s => ({ ...s, role: v }))}>
                <SelectTrigger data-testid="create-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrateur</SelectItem>
                  <SelectItem value="commercial">Commercial</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleCreateUser} disabled={creating} className="bg-[#1a3a5c] hover:bg-[#15304d]" data-testid="create-user-submit">
              {creating ? "Création..." : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={pwdDialogOpen} onOpenChange={(open) => { if (!open) setPwdDialogOpen(false); }}>
        <DialogContent className="sm:max-w-md" data-testid="change-pwd-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow text-lg uppercase tracking-tight flex items-center gap-2">
              <KeyRound className="w-5 h-5" /> Modifier le mot de passe
            </DialogTitle>
            <DialogDescription>
              {pwdTarget ? `Changer le mot de passe de ${pwdTarget.name} (${pwdTarget.email})` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Nouveau mot de passe (min. 6 caractères)</Label>
              <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="••••••" data-testid="new-password-input" />
            </div>
            <p className="text-xs text-slate-400">La connexion Google de cet utilisateur ne sera pas affectée. Le mot de passe permet une connexion alternative par email.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwdDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleChangePassword} disabled={changingPwd} className="bg-amber-600 hover:bg-amber-700" data-testid="change-pwd-submit">
              {changingPwd ? "Modification..." : "Modifier"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
